import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import EDIDropzone from '../components/EDIDropzone';
import EDICsvImport from '../components/EDICsvImport';
import CSVOrderImport from '../components/CSVOrderImport';
import CSVImportSettings from '../components/CSVImportSettings';
import ReturnsImport from '../components/ReturnsImport';
import CSVGenerator from '../utilities/CSVGenerator';
import Styles from './EDIOrdersImport.module.css';

/**
 * Main application container with tabs for EDI conversion and CSV order import.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const EDIOrdersImport = () => {
    const [activeTab, setActiveTab] = useState('edi');
    const [files, setFiles] = useState([]);
    
    // Get environment from environment variables
    // eslint-disable-next-line no-unused-vars
    const apiMode = process.env.REACT_APP_API_MODE || 'development';

    const onDrop = useCallback(acceptedFiles => {
        setFiles(acceptedFiles);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

    const handleCancel = () => {
        setFiles([]);
    }

    const handleConvertEdiToCsv = async (onFinish) => {
        CSVGenerator.run(files, onFinish);
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'edi':
                return (
                    <div className={Styles.ediContainer}>
                        <div className={Styles.header}>
                            <h1>BabyOne Import</h1>
                            <p>EDI Dateien von BabyOne als Bestellungen anlegen</p>
                        </div>
                        <EDIDropzone getRootProps={getRootProps} getInputProps={getInputProps} isDragActive={isDragActive} />
                        <EDICsvImport files={files} handleCancel={handleCancel} handleConvertEdiToCsv={handleConvertEdiToCsv} />
                    </div>
                );
            case 'csv-import':
                return <CSVOrderImport />;
            case 'csv-settings':
                return <CSVImportSettings />;
            case 'returns-import':
                return <ReturnsImport />;
            default:
                return null;
        }
    };

    // Get environment badge styling based on API mode
    const getEnvironmentBadgeClass = () => {
        switch (apiMode) {
            case 'production':
                return `${Styles.envBadge} ${Styles.envProduction}`;
            case 'staging':
                return `${Styles.envBadge} ${Styles.envStaging}`;
            case 'mock':
                return `${Styles.envBadge} ${Styles.envMock}`;
            default:
                return `${Styles.envBadge} ${Styles.envDevelopment}`;
        }
    };

    return (
        <div className={Styles.container}>
            <div className={Styles.tabNavigation}>
                <div className={Styles.tabButtons}>
                    <button 
                        className={`${Styles.tabButton} ${activeTab === 'edi' ? Styles.active : ''}`}
                        onClick={() => setActiveTab('edi')}
                    >
                        BabyOne Import
                    </button>
                    <button 
                        className={`${Styles.tabButton} ${activeTab === 'csv-import' ? Styles.active : ''}`}
                        onClick={() => setActiveTab('csv-import')}
                    >
                        CSV Import
                    </button>
                    <button
                        className={`${Styles.tabButton} ${activeTab === 'csv-settings' ? Styles.active : ''}`}
                        onClick={() => setActiveTab('csv-settings')}
                    >
                        Einstellungen
                    </button>
                    <button
                        className={`${Styles.tabButton} ${activeTab === 'returns-import' ? Styles.active : ''}`}
                        onClick={() => setActiveTab('returns-import')}
                    >
                        Retourenimport
                    </button>
                </div>
                <div className={getEnvironmentBadgeClass()}>
                    {apiMode.toUpperCase()}
                </div>
            </div>
            <div className={Styles.tabContent}>
                {renderTabContent()}
            </div>
        </div>
    )
}

export default EDIOrdersImport;
